import { Icons, TextInput, Tooltip } from "@osttra/ds-react";
import React, { useCallback, useState } from "react";

type LoginInputProps = {
  filteredEmails: string[];
  username: string;
  setUsername: React.Dispatch<React.SetStateAction<string>>;
  isValidEmail: boolean;
  handleRecentEmailDeleteClick: (emailToRemove: string) => void;
};

const LoginInput = ({
  filteredEmails,
  setUsername,
  username,
  isValidEmail,
  handleRecentEmailDeleteClick,
}: LoginInputProps) => {
  const [tooltipKey, setTooltipKey] = useState(0);

  const [activeIndex, setActiveIndex] = useState(-1);

  const handleRecentEmailClick = useCallback(
    (email: string) => {
      setUsername(email);
      setTooltipKey((prev) => prev + 1);
      setActiveIndex(-1);
    },
    [setUsername]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const listLength = filteredEmails.length;
      if (listLength === 0) return;

      const handlers: Record<string, () => void> = {
        ArrowDown: () => {
          e.preventDefault();
          setActiveIndex((prev) => (prev + 1) % listLength);
        },
        ArrowUp: () => {
          e.preventDefault();
          setActiveIndex((prev) => (prev - 1 + listLength) % listLength);
        },
        Tab: () => {
          e.preventDefault();
          setActiveIndex((prev) => (e.shiftKey ? (prev - 1 + listLength) % listLength : (prev + 1) % listLength));
        },
        Enter: () => {
          if (activeIndex >= 0) {
            e.preventDefault();
            e.stopPropagation();
            handleRecentEmailClick(filteredEmails[activeIndex]);
          }
        },
        Delete: () => {
          if (activeIndex >= 0) {
            e.preventDefault();
            handleRecentEmailDeleteClick(filteredEmails[activeIndex]);
          }
        },
        Backspace: () => {
          if (activeIndex >= 0) {
            e.preventDefault();
            handleRecentEmailDeleteClick(filteredEmails[activeIndex]);
          }
        },
        Escape: () => {
          setActiveIndex(-1);
          setTooltipKey((prev) => prev + 1);
        },
      };

      handlers[e.key]?.();
    },
    [filteredEmails, activeIndex, handleRecentEmailClick, handleRecentEmailDeleteClick]
  );

  const tooltipContent = () => (
    <div className="recent-email-container">
      {filteredEmails.map((email, index) => (
        <div
          key={index}
          className={`recent-email-item ${index === activeIndex ? "active-highlight" : ""}`}
          onMouseEnter={() => setActiveIndex(index)}
          onMouseDown={(e) => {
            e.preventDefault();
            handleRecentEmailClick(email);
          }}
        >
          <span className="recent-email-id">{email}</span>
          <span
            className="recent-email-delete"
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleRecentEmailDeleteClick(email);
            }}
          >
            <Icons.Trash />
          </span>
        </div>
      ))}
    </div>
  );

  return (
    <div className="field" onKeyDown={handleKeyDown}>
      <Tooltip
        key={tooltipKey}
        trigger="click"
        placement="bottom-start"
        message={tooltipContent()}
        className={`recent-email-tooltip ${filteredEmails.length === 0 ? "tooltip-disabled" : ""}`}
      >
        <TextInput
          value={username}
          icon="user"
          inputAlign="left"
          placeholder="Enter email address"
          type="email"
          valid={isValidEmail}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => setUsername(event.target.value)}
          autoComplete="one-time-code"
        />
      </Tooltip>
    </div>
  );
};

export default LoginInput;
